import React, { useState, useEffect } from 'react';
import './PostAd.css';
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../firebase/config";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from '../context/AuthContext';
import { uploadToCloudinary } from '../utils/cloudinary';
import { slugify } from '../utils/adUtils';
import Spinner from '../components/Spinner';

const EditAd = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();

    const [formData, setFormData] = useState({
        title: '',
        vehicleCondition: 'Used',
        vehicleType: 'Car',
        vehicleMake: '',
        model: '',
        manufacturedYear: '',
        price: '',
        transmission: 'Automatic',
        fuelType: 'Petrol',
        engineCapacity: '',
        mileage: '',
        location: '',
        description: '',
        useAccountPhone: true,
        customPhone: '',
        options: {
            airCondition: false,
            powerSteering: false,
            powerMirror: false,
            powerWindow: false
        }
    });

    const [existingImages, setExistingImages] = useState([]);
    const [newImages, setNewImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');

    // Field Options
    const vehicleConditions = ["New", "Used", "Reconditioned"];
    const vehicleTypes = ["Car", "Van", "SUV", "Jeep", "Motorbike", "Three Wheeler", "Truck", "Bus", "Lorry", "Bicycle", "Parts & Accessories"];
    const transmissions = ["Automatic", "Manual", "Tiptronic"];
    const fuelTypes = ["Petrol", "Diesel", "Hybrid", "Electric", "None"];
    const years = Array.from({ length: 50 }, (_, i) => new Date().getFullYear() - i);

    useEffect(() => {
        const fetchAd = async () => {
            try {
                const docRef = doc(db, "ads", id);
                const docSnap = await getDoc(docRef);

                if (docSnap.exists()) {
                    const data = docSnap.data();

                    // Security check: only owner or admin can edit
                    if (user && data.userId !== user.uid && user.role !== 'admin') {
                        setError("You are not authorized to edit this ad.");
                        setLoading(false);
                        return;
                    }

                    setFormData({
                        title: data.title || '',
                        vehicleCondition: data.vehicleCondition || 'Used',
                        vehicleType: data.vehicleType || 'Car',
                        vehicleMake: data.vehicleMake || '',
                        model: data.model || '',
                        manufacturedYear: data.manufacturedYear || '',
                        price: data.price || '',
                        transmission: data.transmission || 'Automatic',
                        fuelType: data.fuelType || 'Petrol',
                        engineCapacity: data.engineCapacity || '',
                        mileage: data.mileage || '',
                        location: data.location || '',
                        description: data.description || '',
                        useAccountPhone: !data.userPhone || data.userPhone === user?.phone,
                        customPhone: (data.userPhone && data.userPhone !== user?.phone) ? data.userPhone : '',
                        options: data.options || {
                            airCondition: false,
                            powerSteering: false,
                            powerMirror: false,
                            powerWindow: false
                        }
                    });
                    setExistingImages(data.images || []);
                    setSelectedCategory(data.category || '');
                } else {
                    setError("Ad not found.");
                }
            } catch (err) {
                console.error("Error fetching ad:", err);
                setError("Failed to load ad details.");
            } finally {
                setLoading(false);
            }
        };

        if (user) {
            fetchAd();
        }
    }, [id, user]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (name in formData.options) {
            setFormData(prev => ({
                ...prev,
                options: { ...prev.options, [name]: checked }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleImageChange = (e) => {
        if (e.target.files) {
            const filesArray = Array.from(e.target.files);
            if (filesArray.length + newImages.length + existingImages.length > 6) {
                alert("You can only have up to 6 images in total.");
                return;
            }
            setNewImages(prev => [...prev, ...filesArray]);
        }
    };

    const removeExistingImage = (index) => {
        setExistingImages(prev => prev.filter((_, i) => i !== index));
    };

    const handleDragStart = (e, index) => {
        e.dataTransfer.setData("imageIndex", index);
    };

    const handleDrop = (e, targetIndex) => {
        e.preventDefault();
        const sourceIndex = e.dataTransfer.getData("imageIndex");
        if (sourceIndex === "" || Number(sourceIndex) === targetIndex) return;

        setExistingImages(prev => {
            const arr = [...prev];
            const [draggedItem] = arr.splice(Number(sourceIndex), 1);
            arr.splice(targetIndex, 0, draggedItem);
            return arr;
        });
    };

    const removeNewImage = (index) => {
        setNewImages(prev => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError('');

        try {
            // Determine the final title (auto-generate for standard vehicles if empty)
            let finalTitle = formData.title;
            if (!finalTitle && (selectedCategory === 'Sell' || !selectedCategory || selectedCategory === 'Rent' || selectedCategory === 'Rent Vehicle')) {
                finalTitle = `${formData.vehicleMake} ${formData.model}`.trim();
            }
            if (!finalTitle) finalTitle = 'Vehicle Ad';

            // Upload new images to Cloudinary with SEO-friendly names from the ad title
            const uploadedUrls = [];
            for (let i = 0; i < newImages.length; i++) {
                // Offset index by existing images count so naming stays sequential
                const url = await uploadToCloudinary(newImages[i], finalTitle, existingImages.length + i);
                if (url) uploadedUrls.push(url);
            }

            const allImageUrls = [...existingImages, ...uploadedUrls];

            // Fallback image if none
            const mainImage = allImageUrls.length > 0
                ? allImageUrls[0]
                : 'https://placehold.co/600x400/1e293b/ffffff?text=' + encodeURIComponent(finalTitle);

            const adRef = doc(db, "ads", id);
            const updateData = {
                ...formData,
                title: finalTitle, // Ensure title is updated in DB
                userPhone: formData.useAccountPhone ? (user?.phone || 'N/A') : formData.customPhone,
                image: mainImage,
                images: allImageUrls,
                updatedAt: new Date()
            };

            // Maintain/Update slugs for Corporate vendors and Admins
            if (user?.userType === 'Corporate' || user?.role === 'admin') {
                updateData.vendorSlug = user.slug || (user.role === 'admin' ? 'riyamaga' : '');
                updateData.adSlug = slugify(finalTitle);
            }

            await updateDoc(adRef, updateData);

            alert('Ad updated successfully!');
            navigate('/profile');
        } catch (err) {
            console.error("Error updating ad: ", err);
            setError('Failed to update ad. Please try again. ' + err.message);
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return <Spinner fullPage message="Loading ad details..." />;
    if (error) return <div className="error-container">{error} <button onClick={() => navigate('/profile')}>Go Back</button></div>;

    return (
        <div className="post-ad-container">
            <h2 className="section-title">Edit Ad - {selectedCategory}</h2>

            <form onSubmit={handleSubmit} className="post-ad-form">
                <button type="button" className="btn-back" onClick={() => navigate('/profile')}>← Cancel Edit</button>
                {error && <div className="error-message">{error}</div>}

                {/* Basic Info */}
                <div className="form-row">
                    <div className="form-group">
                        <label>Vehicle Type</label>
                        <select name="vehicleType" value={formData.vehicleType} onChange={handleChange} required={user?.role !== 'admin'}>
                            {vehicleTypes.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                    </div>
                    <div className="form-group">
                        <label>Condition</label>
                        <select name="vehicleCondition" value={formData.vehicleCondition} onChange={handleChange} required={user?.role !== 'admin'}>
                            {vehicleConditions.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label>Make</label>
                        <input type="text" name="vehicleMake" value={formData.vehicleMake} onChange={handleChange} placeholder="e.g. Toyota" required={user?.role !== 'admin'} />
                    </div>
                    <div className="form-group">
                        <label>Model</label>
                        <input type="text" name="model" value={formData.model} onChange={handleChange} placeholder="e.g. Aqua" required={user?.role !== 'admin'} />
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label>Manufactured Year</label>
                        <select name="manufacturedYear" value={formData.manufacturedYear} onChange={handleChange} required={user?.role !== 'admin'}>
                            <option value="">Select Year</option>
                            {years.map(y => <option key={y} value={y}>{y}</option>)}
                        </select>
                    </div>
                    <div className="form-group">
                        <label>Transmission</label>
                        <select name="transmission" value={formData.transmission} onChange={handleChange} required={user?.role !== 'admin'}>
                            {transmissions.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label>Fuel Type</label>
                        <select name="fuelType" value={formData.fuelType} onChange={handleChange} required={user?.role !== 'admin'}>
                            {fuelTypes.map(f => <option key={f} value={f}>{f}</option>)}
                        </select>
                    </div>
                    <div className="form-group">
                        <label>Engine Capacity (cc)</label>
                        <input type="number" name="engineCapacity" value={formData.engineCapacity} onChange={handleChange} placeholder="e.g. 1500" />
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label>Mileage (km)</label>
                        <input type="number" name="mileage" value={formData.mileage} onChange={handleChange} placeholder="e.g. 50000" required={user?.role !== 'admin'} />
                    </div>
                    <div className="form-group">
                        <label>Location</label>
                        <input type="text" name="location" value={formData.location} onChange={handleChange} placeholder="e.g. Colombo" required={user?.role !== 'admin'} />
                    </div>
                </div>

                <div className="form-group">
                    <label>Title (for Search Results)</label>
                    <input type="text" name="title" value={formData.title} onChange={handleChange} placeholder="e.g. Toyota Aqua 2018 for Sale" required={user?.role !== 'admin'} />
                </div>

                <div className="form-group">
                    <label>Price (Rs.) {selectedCategory === 'Rent Vehicle' ? 'per Month/Day' : ''}</label>
                    <input type="text" name="price" value={formData.price} onChange={handleChange} placeholder="e.g. 6,500,000" required={user?.role !== 'admin'} />
                </div>

                <div className="form-group">
                    <label>Description (Additional Info)</label>
                    <textarea name="description" value={formData.description} onChange={handleChange} rows="4"></textarea>
                </div>

                <div className="form-group contact-phone-section" style={{ background: 'rgba(59, 130, 246, 0.05)', padding: '1.5rem', borderRadius: '12px', border: '1px solid rgba(59, 130, 246, 0.1)', marginBottom: '1.5rem' }}>
                    <label style={{ fontWeight: 600, color: 'var(--color-accent)', marginBottom: '1rem', display: 'block' }}>
                        <i className="fa-solid fa-phone-volume"></i> Contact Number for this Ad
                    </label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                            <input
                                type="radio"
                                name="useAccountPhone"
                                checked={formData.useAccountPhone}
                                onChange={() => setFormData(prev => ({ ...prev, useAccountPhone: true }))}
                            />
                            <span>Use my account number ({user?.phone || 'N/A'})</span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                            <input
                                type="radio"
                                name="useAccountPhone"
                                checked={!formData.useAccountPhone}
                                onChange={() => setFormData(prev => ({ ...prev, useAccountPhone: false }))}
                            />
                            <span>Use a different number</span>
                        </label>

                        {!formData.useAccountPhone && (
                            <div style={{ marginTop: '0.5rem' }}>
                                <input
                                    type="text"
                                    name="customPhone"
                                    value={formData.customPhone}
                                    onChange={handleChange}
                                    placeholder="Enter contact number (e.g. 077 123 4567)"
                                    required={!formData.useAccountPhone && user?.role !== 'admin'}
                                    style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid var(--color-border)' }}
                                />
                            </div>
                        )}
                    </div>
                </div>

                {/* Options Checkboxes */}
                <div className="form-group">
                    <label>Options</label>
                    <div className="checkbox-group">
                        <label><input type="checkbox" name="airCondition" checked={formData.options.airCondition} onChange={handleChange} /> Air Condition</label>
                        <label><input type="checkbox" name="powerSteering" checked={formData.options.powerSteering} onChange={handleChange} /> Power Steering</label>
                        <label><input type="checkbox" name="powerMirror" checked={formData.options.powerMirror} onChange={handleChange} /> Power Mirror</label>
                        <label><input type="checkbox" name="powerWindow" checked={formData.options.powerWindow} onChange={handleChange} /> Power Window</label>
                    </div>
                </div>

                {/* Image Management */}
                <div className="form-group">
                    <label>Manage Images (Max 6 total)</label>

                    {/* Existing Images */}
                    <div className="image-preview-container" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '1rem', marginTop: '1rem' }}>
                        {existingImages.map((url, index) => (
                            <div
                                key={`existing-${index}`}
                                className="image-preview existing"
                                draggable
                                onDragStart={(e) => handleDragStart(e, index)}
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={(e) => handleDrop(e, index)}
                                style={{ position: 'relative', border: '1px solid var(--color-border)', borderRadius: '8px', padding: '0.5rem', display: 'flex', flexDirection: 'column', alignItems: 'center', background: 'var(--color-card-bg)', cursor: 'move' }}
                            >
                                {index === 0 && (
                                    <div style={{ position: 'absolute', top: '-10px', background: 'var(--color-accent)', color: 'white', fontSize: '0.65rem', padding: '2px 8px', borderRadius: '12px', fontWeight: 'bold' }}>
                                        Preview Cover
                                    </div>
                                )}
                                <img src={url} alt="Existing" style={{ width: '100%', height: '80px', objectFit: 'contain', borderRadius: '4px', marginBottom: '0.5rem' }} draggable="false" />
                                <button type="button" onClick={() => removeExistingImage(index)} className="btn-remove" style={{ color: 'red', marginTop: '0.5rem', width: '100%', padding: '0.2rem', cursor: 'pointer' }}><i className="fa-solid fa-trash"></i> Remove</button>
                            </div>
                        ))}
                    </div>

                    {/* New Uploads */}
                    <input type="file" multiple accept="image/*" onChange={handleImageChange} style={{ marginTop: '2rem' }} />
                    {newImages.length > 0 && (
                        <div className="image-preview-container" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '1rem', marginTop: '1rem' }}>
                            {newImages.map((file, index) => (
                                <div key={`new-${index}`} className="image-preview new" style={{ position: 'relative', border: '1px solid var(--color-border)', borderRadius: '8px', padding: '0.5rem', display: 'flex', flexDirection: 'column', alignItems: 'center', background: 'var(--color-card-bg)' }}>
                                    <div style={{ height: '80px', width: '100%', display: 'flex', justifyContent: 'center', marginBottom: '0.5rem' }}>
                                        <img src={URL.createObjectURL(file)} alt="New Upload" style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }} />
                                    </div>
                                    <span style={{ fontSize: '0.7rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%', textAlign: 'center' }}>
                                        {file.name}
                                    </span>
                                    <button type="button" onClick={() => removeNewImage(index)} className="btn-remove" style={{ color: 'red', marginTop: '0.5rem', width: '100%' }}><i className="fa-solid fa-trash"></i> Remove</button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <button type="submit" className="btn-submit" disabled={submitting}>
                    {submitting ? 'Updating...' : 'Save Changes'}
                </button>
            </form>
        </div>
    );
};

export default EditAd;
